import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';

import {ApiService} from '../../services/api.service';
import {QuestionnaireEditorService} from '../../services/questionnaire-editor.service';
import {QuestionnaireFormatService} from '../../services/questionnaire-format.service';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {KEYS} from '../../../translation-keys';

interface AiChatMessage {
  seq: number;
  role: string;
  kind: string;
  text: string;
  createdAt: string;
}

interface AiActivity {
  tool: string;
  detail: string | null;
}

interface AiUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  estimatedCost: number;
}

interface AiAttachment {
  name: string;
  mime: string;
  data: string;
  sizeBytes: number;
}

const ATTACHMENT_MIMES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

/**
 * Maps raw tool names of the questionnaire-generator profile to translation keys.
 * The agent server delivers raw tool names; presentation is profile specific and
 * therefore lives in the client.
 */
const TOOL_LABEL_KEYS: {[toolName: string]: string} = {
  propose_plan: KEYS.EDITOR.AI_TOOL_PROPOSE_PLAN,
  read_schema_docs: KEYS.EDITOR.AI_TOOL_READ_SCHEMA_DOCS,
  bash: KEYS.EDITOR.AI_TOOL_BASH,
  write: KEYS.EDITOR.AI_TOOL_WRITE,
  edit: KEYS.EDITOR.AI_TOOL_EDIT,
  read: KEYS.EDITOR.AI_TOOL_ANALYZE,
  grep: KEYS.EDITOR.AI_TOOL_ANALYZE,
  find: KEYS.EDITOR.AI_TOOL_ANALYZE,
  ls: KEYS.EDITOR.AI_TOOL_ANALYZE,
  read_document: KEYS.EDITOR.AI_TOOL_READ_DOCUMENT,
  validate_questionnaire: KEYS.EDITOR.AI_TOOL_VALIDATE,
  submit_questionnaire: KEYS.EDITOR.AI_TOOL_SUBMIT
};

// hack to inject decorator declarations. must occur before class declaration!
export interface AiAssistantComponent extends Translatable {}

/**
 * Chat overlay for the ai questionnaire assistant. Creates agent sessions via the questionnaire
 * api, polls their state and applies generated questionnaires to the editor.
 */
@Component({
  selector: 'app-qa-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class AiAssistantComponent implements OnChanges, OnDestroy {
  @Input() questionnaire: Questionnaire;
  @Input() pathHashMap: any;
  @Input() locale = 'de_DE';
  @Input() visible = false;
  @Output() onHide: EventEmitter<any> = new EventEmitter();
  @Output() onImportQuestionnaire: EventEmitter<Questionnaire> = new EventEmitter();

  @ViewChild('messageList') messageList: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  public messages: AiChatMessage[] = [];
  public attachments: AiAttachment[] = [];
  public status: string = null;
  public activity: AiActivity = null;
  public toolCalls = 0;
  public usage: AiUsage = null;
  public activeMs = 0;
  public promptText = '';
  public busy = false;
  public errorMessage: string = null;
  public result: any = null;

  private sessionId: string = null;
  private lastSeq = 0;
  private resultVersion = -1;
  private pollTimer: any = null;
  private polling = false;

  public constructor(
    private apiService: ApiService,
    private questionnaireEditorService: QuestionnaireEditorService,
    private questionnaireFormatService: QuestionnaireFormatService,
    @Optional() private translationService: TranslationService
  ) {
  }

  /**
   * Resumes polling when the overlay becomes visible again while a session exists
   */
  public ngOnChanges(changes: SimpleChanges) {
    if (changes.visible && this.visible && this.sessionId) {
      this.startPolling();
    }
  }

  /**
   * Cleans up the polling timer and the remote session
   */
  public ngOnDestroy() {
    this.stopPolling();

    if (this.sessionId) {
      this.apiService.deleteAiSession(this.sessionId).catch(() => null);
    }
  }

  /**
   * Returns true when the assistant is waiting for the agent
   */
  public get isThinking(): boolean {
    return this.busy || this.status === 'starting' || this.status === 'thinking';
  }

  /**
   * Returns true when the agent proposed a plan and waits for confirmation
   */
  public get isAwaitingConfirmation(): boolean {
    return this.status === 'awaiting_confirmation';
  }

  /**
   * Returns the translated label for the current agent activity
   */
  public getActivityLabel(): string {
    const key = this.activity ? TOOL_LABEL_KEYS[this.activity.tool] : null;
    return key ? this._(key) : this._(KEYS.EDITOR.AI_WORKING);
  }

  /**
   * Formats the active working time as m:ss
   */
  public formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  /**
   * Formats a token count with thousands separators
   */
  public formatTokens(tokens: number): string {
    return (tokens || 0).toLocaleString();
  }

  /**
   * Formats the estimated cost in usd
   */
  public formatCost(cost: number): string {
    if (!cost) {
      return '$0.00';
    }
    return '$' + (cost < 0.1 ? cost.toFixed(3) : cost.toFixed(2));
  }

  /**
   * Sends the current prompt text and pending attachments: creates a session on first use,
   * otherwise sends a message
   */
  public onSend() {
    const text = (this.promptText || '').trim();
    const attachments = this.attachments;

    if ((!text && !attachments.length) || this.isThinking) {
      return;
    }

    // attachments without text get a sensible default prompt
    const messageText = text || this._(KEYS.EDITOR.AI_ATTACHMENT_PROMPT);
    const files = attachments.map((attachment) => ({name: attachment.name, mime: attachment.mime, data: attachment.data}));

    this.promptText = '';
    this.attachments = [];
    this.errorMessage = null;
    this.busy = true;

    const request = this.sessionId
      ? this.apiService.sendAiSessionMessage(this.sessionId, messageText, files)
      : this.apiService.createAiSession(messageText, this.getContextPayload(), this.locale, files);

    request
      .then((response: any) => this.applySessionState(response.session))
      .catch((error) => {
        // keep the attachments so the user can simply retry
        this.attachments = attachments;
        this.handleError(error);
      })
      .then(() => {
        this.busy = false;
        this.startPolling();
      });
  }

  /**
   * Opens the file picker for attachments
   */
  public onAttachClick() {
    if (this.isThinking || !this.fileInput) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  /**
   * Validates selected files and queues them as base64 attachments
   */
  public onFilesSelected(event: any) {
    const files: FileList = event.target.files;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (this.attachments.length >= MAX_ATTACHMENTS) {
        this.errorMessage = this._(KEYS.EDITOR.AI_TOO_MANY_FILES);
        break;
      }

      if (ATTACHMENT_MIMES.indexOf(file.type) === -1) {
        this.errorMessage = `${this._(KEYS.EDITOR.AI_FILE_UNSUPPORTED)}: ${file.name}`;
        continue;
      }

      if (file.size > MAX_ATTACHMENT_BYTES) {
        this.errorMessage = `${this._(KEYS.EDITOR.AI_FILE_TOO_LARGE)}: ${file.name}`;
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        // readAsDataURL delivers "data:<mime>;base64,<data>"; the backend expects plain base64
        const data = String(reader.result).split(',')[1] || '';
        this.attachments.push({name: file.name, mime: file.type, data, sizeBytes: file.size});
      };
      reader.readAsDataURL(file);
    }

    // allow re-selecting the same file later
    event.target.value = '';
  }

  /**
   * Removes a queued attachment
   */
  public removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }

  /**
   * Formats an attachment size for the chip label
   */
  public formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /**
   * Confirms the proposed plan
   */
  public onConfirmPlan() {
    if (!this.sessionId || this.isThinking) {
      return;
    }

    this.errorMessage = null;
    this.busy = true;

    this.apiService.sendAiSessionMessage(this.sessionId, this._(KEYS.EDITOR.AI_CONFIRM_MESSAGE))
      .then((response: any) => this.applySessionState(response.session))
      .catch((error) => this.handleError(error))
      .then(() => {
        this.busy = false;
        this.startPolling();
      });
  }

  /**
   * Applies the generated questionnaire to the editor
   */
  public onApply() {
    if (!this.result) {
      return;
    }

    this.questionnaireEditorService
      .loadQuestionnaireByJson(JSON.stringify(this.result), this.pathHashMap)
      .then(questionnaire => {
        // generated questionnaires carry no author; assign the current user like the editor
        // does for new questionnaires. an existing author (edited questionnaire) is kept.
        if (questionnaire.meta && !questionnaire.meta.author) {
          questionnaire.meta.author = this.questionnaireEditorService.getLoggedInUserName();
        }

        // the editor tracks question components by question id. generated questionnaires keep
        // ids stable across revisions, so applying twice would reuse component instances and
        // skip their ngOnInit normalization (e.g. missing description). emitting an empty
        // question list first destroys the components, so they get recreated cleanly.
        this.onImportQuestionnaire.emit(<any>{...(<any>questionnaire), questions: []});

        setTimeout(() => {
          this.onImportQuestionnaire.emit(questionnaire);
          alert(this._(KEYS.EDITOR.AI_APPLIED));
          this.hide();
        });
      })
      .catch(error => {
        this.errorMessage = `${this._(KEYS.EDITOR.AI_SESSION_ERROR)}: ${error.message}`;
      });
  }

  /**
   * Discards the current session and starts a fresh conversation
   */
  public onNewSession() {
    if (this.sessionId) {
      this.apiService.deleteAiSession(this.sessionId).catch(() => null);
    }

    this.stopPolling();
    this.sessionId = null;
    this.lastSeq = 0;
    this.resultVersion = -1;
    this.messages = [];
    this.attachments = [];
    this.status = null;
    this.activity = null;
    this.toolCalls = 0;
    this.usage = null;
    this.activeMs = 0;
    this.result = null;
    this.errorMessage = null;
  }

  /**
   * Hides the overlay. The session keeps running and can be resumed by reopening the overlay.
   */
  public hide() {
    this.onHide.emit();
  }

  /**
   * Handles the enter key in the prompt textarea (send on enter, newline on shift+enter)
   */
  public onPromptKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  /**
   * Builds the context payload of the currently edited questionnaire, or null when it is empty
   */
  private getContextPayload(): any {
    const hasContent = this.questionnaire && this.questionnaire.questions && this.questionnaire.questions.length > 0;

    if (!hasContent) {
      return null;
    }

    return this.questionnaireFormatService.getPayload(this.questionnaire);
  }

  /**
   * Merges a session state response into the component state
   */
  private applySessionState(session: any) {
    if (!session) {
      return;
    }

    this.sessionId = session.sessionId;
    this.status = session.status;
    this.activity = session.activity;
    this.toolCalls = session.toolCalls || 0;
    this.usage = session.usage || this.usage;
    this.activeMs = session.activeMs || this.activeMs;

    if (session.error) {
      this.errorMessage = session.error;
    }

    // the result payload is only transferred once per version; remember what we have
    if (session.result) {
      this.result = session.result;
    }
    if (typeof session.resultVersion === 'number') {
      this.resultVersion = session.resultVersion;
    }

    const messages = session.messages || [];
    for (const message of messages) {
      if (message.seq <= this.lastSeq) {
        continue;
      }

      this.lastSeq = message.seq;

      // tool activity is shown via the live status line; log the full sequence for debugging only
      if (message.role === 'tool') {
        console.debug(`[ai-assistant] step ${this.toolCalls}: ${message.text}`);
        continue;
      }

      this.messages.push(message);
    }

    if (messages.length) {
      this.scrollToBottom();
    }
  }

  /**
   * Starts polling the session state. Polls run strictly sequential: the next request is
   * only scheduled after the previous response arrived, so slow responses can never pile up.
   * Polling keeps running while the overlay is visible: the agent may report transient idle
   * states between internal runs (retries, compaction), so a settled status snapshot is no
   * reliable signal that the work is finished.
   */
  private startPolling() {
    if (this.polling || !this.sessionId) {
      return;
    }

    this.polling = true;
    this.scheduleNextPoll();
  }

  /**
   * Schedules the next poll request
   */
  private scheduleNextPoll() {
    if (!this.polling) {
      return;
    }

    this.pollTimer = setTimeout(() => this.poll(), 1500);
  }

  /**
   * Executes a single poll request and schedules the next one after the response arrived
   */
  private poll() {
    if (!this.polling || !this.sessionId) {
      return;
    }

    this.apiService.getAiSession(this.sessionId, this.lastSeq, this.resultVersion)
      .then((response: any) => {
        this.applySessionState(response.session);

        // only stop when the panel is hidden and nothing is running anymore
        if (!this.visible && !this.isThinking) {
          this.stopPolling();
          return;
        }

        this.scheduleNextPoll();
      })
      .catch((error) => {
        this.stopPolling();
        this.handleError(error);
      });
  }

  /**
   * Stops polling the session state
   */
  private stopPolling() {
    this.polling = false;

    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Normalizes an http error into the error message display
   */
  private handleError(error: any) {
    const message = error && error.error && error.error.message
      ? error.error.message
      : (error && error.message ? error.message : 'unknown error');

    this.errorMessage = `${this._(KEYS.EDITOR.AI_SESSION_ERROR)}: ${message}`;
    this.status = this.sessionId ? this.status : null;
  }

  /**
   * Scrolls the message list to the newest entry
   */
  private scrollToBottom() {
    setTimeout(() => {
      if (this.messageList && this.messageList.nativeElement) {
        this.messageList.nativeElement.scrollTop = this.messageList.nativeElement.scrollHeight;
      }
    }, 50);
  }
}
